import { Injectable, inject } from '@angular/core';
import { GlobalService } from './global.service';
import { vcFGComponent } from '../../features/configurator/vas-configurator.constants';

@Injectable({
  providedIn: 'root',
})
export class Calculation {
  private globalService = inject(GlobalService);
  calculateExtendedValue(quantity: null | any, unitValue: null | any): null | any {
    unitValue = this.globalService.getOnlyValue(unitValue);
    if (quantity === null || unitValue === null) {
      return null;
    }
    const result = +quantity * unitValue;
    return this.preciseRound(result, 4);
  }

  calculateMargin(price: any, cost: any): any {
    if (price === null && cost === null) {
      return null;
    }

    price = this.globalService.getOnlyValue(price);
    cost = this.globalService.getOnlyValue(cost);
    if (price === cost) {
      return null;
    }
    return ((price - cost) / price) * 100;
  }

  calculatePrice(cost: any, margin: any): any {
    cost = this.globalService.getOnlyValue(cost);
    margin = this.globalService.getOnlyMarginValue(margin);
    return cost / (1 - margin / 100);
  }

  preciseRound(num: any, decimals: any, isMargin?: boolean) {
    if (num === null) {
      return null;
    }
    if (typeof num === 'string') {
      if (isMargin) num = this.globalService.getOnlyMarginValue(num);
      else num = this.globalService.getOnlyValue(num);
    }
    const multiplier = Math.pow(10, decimals);
    return Math.round((num + Number.EPSILON) * multiplier) / multiplier;
  }

  applyFgPriceRounding(num: any, factor: number, type: string): any {
    if (num === null) return null;
    if (typeof num === 'string') num = this.globalService.getOnlyValue(num);
    if (isNaN(num)) return null;
    if (type === 'Round Up') {
      const multiplier = Math.pow(10, factor);
      return Math.ceil((num + Number.EPSILON) * multiplier) / multiplier;
    }
    return this.preciseRound(num, factor);
  }

  rollUp(components: Array<any>): {
    Cost: any;
    Price: any;
    BMPercentage?: any;
    GP?: any;
    AverageCost?: any;
  } {
    let cost: any = null;
    let price: any = null;
    let averageCost: any = null;

    for (const comp of components) {
      if (comp.FinishedGoodLineItemId || comp.PartNumber) {
        // PartNumber for iframe as identifier
        cost =
          cost === null
            ? this.globalService.getOnlyValue(comp.ExtendedCost)
            : cost + this.globalService.getOnlyValue(comp.ExtendedCost);
        price =
          price === null
            ? this.globalService.getOnlyValue(comp.ExtendedPrice)
            : price + this.globalService.getOnlyValue(comp.ExtendedPrice);
        averageCost =
          averageCost === null
            ? this.globalService.getOnlyValue(comp.ExtendedAverageCost)
            : averageCost + this.globalService.getOnlyValue(comp.ExtendedAverageCost);
      }
    }
    const margin = this.calculateMargin(price, cost);

    return {
      Cost: this.preciseRound(cost, 4),
      Price: this.preciseRound(price, 4),
      BMPercentage: this.preciseRound(margin, 4, true),
      GP: this.globalService.formatMargin(margin, true, 4),
      AverageCost: this.preciseRound(averageCost, 4),
    };
  }
  

  rollDown(components: Array<any>, fgValues: { Cost: any; Price: any; BMPercentage: any }) {
    let realMargin = fgValues.BMPercentage;
    
    const noCostComponents = components.filter(
      (component) =>
        !this.globalService.getOnlyValue(component.ExtendedCost) &&
        this.globalService.getOnlyValue(component.ExtendedPrice),
    );
    if (noCostComponents.length) {
      realMargin = this.getRealMargin(noCostComponents, fgValues);
    }
  
    const updatedComponents = components.map((component) => {
      if (!component.FinishedGoodLineItemId) {
        return component;
      }

      const extCost = this.globalService.getOnlyValue(component.ExtendedCost);
      const extPrice = this.globalService.getOnlyValue(component.ExtendedPrice);
      if ((!extCost && extPrice) || (!extCost && !extPrice)) {
        return component;
      }
  
      return {
        ...component,
        BM: this.preciseRound(realMargin, 4, true), // remove BM
        GP: this.preciseRound(realMargin, 4, true),
        UnitPrice: this.preciseRound(this.calculatePrice(component.UnitCost, realMargin), 4),
        ExtendedPrice: this.preciseRound(
          this.calculatePrice(component.ExtendedCost, realMargin),
          4,
        ),
      };
    });
  
    return updatedComponents;
  }

  private applyQuotedCostFallback(child: vcFGComponent): vcFGComponent {
    const hasUnitValues =
      this.globalService.getOnlyValue(child.UnitCost) ||
      this.globalService.getOnlyValue(child.UnitPrice);
    const quotedCost = this.globalService.getOnlyValue(child.QuotedCost);
    if (hasUnitValues || !quotedCost) {
      return child;
    }
    const quantity = this.globalService.getOnlyValue(child.Quantity);
    const extValue = this.calculateExtendedValue(quantity, quotedCost);
    return {
      ...child,
      UnitCost: quotedCost,
      UnitPrice: quotedCost,
      ExtendedCost: extValue,
      ExtendedPrice: extValue,
      GP: this.calculateMargin(quotedCost, quotedCost),
    };
  }

  // Post-order: resolves nested children first, then folds them into this node's own fields —
  // a no-op on leaf/flat data, so arbitrary nesting depth reduces to today's flat behavior.
  rollUpTree(node: vcFGComponent): vcFGComponent {
    if (!node.children || !node.children.length) {
      return node;
    }
    node.children.forEach((child) => this.rollUpTree(child));

    const withFallback = node.children.map((child) => this.applyQuotedCostFallback(child));
    const { Cost, Price, GP, AverageCost } = this.rollUp(withFallback);
    const quantity = this.globalService.getOnlyValue(node.Quantity) ?? 1;

    node.UnitCost = Cost;
    node.UnitPrice = Price;
    node.BM = GP;
    node.GP = GP;
    node.ExtendedCost = this.calculateExtendedValue(quantity, Cost);
    node.ExtendedPrice = this.calculateExtendedValue(quantity, Price);
    node.AverageCost = AverageCost;
    node.ExtendedAverageCost = this.calculateExtendedValue(quantity, AverageCost);
    return node;
  }

  // Pre-order: rolls down one level, then recurses using each child's OWN new Extended/GP as
  // the next level's fgValues — so an outer edit cascades through every nested boundary.
  rollDownTree(
    node: vcFGComponent,
    fgValues: { Cost: any; Price: any; BMPercentage: any },
  ): vcFGComponent {
    if (!node.children || !node.children.length) {
      return node;
    }
    const rolledDownChildren = this.rollDown(node.children, fgValues);
    node.children = this.reconcileChildRounding(rolledDownChildren, fgValues.Price).map((child) =>
      this.rollDownTree(child, {
        Cost: child.ExtendedCost,
        Price: child.ExtendedPrice,
        BMPercentage: child.GP,
      }),
    );
    return node;
  }

  // Compares Extended (not Unit) totals so this stays correct when a child's own Quantity != 1.
  private reconcileChildRounding(
    children: vcFGComponent[],
    targetExtendedPrice: any,
  ): vcFGComponent[] {
    const target = this.globalService.getOnlyValue(targetExtendedPrice);
    if (target === null) {
      return children;
    }
    let sum = 0;
    let lastIndex = -1;
    children.forEach((child, index) => {
      if (!child.FinishedGoodLineItemId) {
        return;
      }
      const extendedPrice = this.globalService.getOnlyValue(child.ExtendedPrice);
      if (extendedPrice === null) {
        return;
      }
      sum += extendedPrice;
      lastIndex = index;
    });
    if (lastIndex === -1) {
      return children;
    }
    const diff = this.preciseRound(target - sum, 4);
    if (!diff) {
      return children;
    }
    const last = children[lastIndex];
    const adjustedExtendedPrice =
      this.preciseRound((this.globalService.getOnlyValue(last.ExtendedPrice) ?? 0) + diff, 4) ?? 0;
    const quantity = this.globalService.getOnlyValue(last.Quantity);
    const adjusted = children.slice();
    adjusted[lastIndex] = {
      ...last,
      ExtendedPrice: adjustedExtendedPrice,
      UnitPrice: quantity
        ? this.preciseRound(adjustedExtendedPrice / quantity, 4)
        : adjustedExtendedPrice,
    };
    return adjusted;
  }

  private getRealMargin(
    noCostComponents: Array<any>,
    fgValues: { Cost: any; Price: any; BMPercentage: any },
  ) {
    let totalNoCostCompPrice = 0;
    for (const comp of noCostComponents) {
      const extPrice = this.globalService.getOnlyValue(comp.ExtendedPrice);
      totalNoCostCompPrice = extPrice ? totalNoCostCompPrice + extPrice : totalNoCostCompPrice;
    }
    const diffPrice =
      fgValues.Price === null
        ? totalNoCostCompPrice
        : (this.globalService.getOnlyValue(fgValues.Price) ?? 0) - totalNoCostCompPrice;
    return this.calculateMargin(diffPrice, fgValues.Cost);
  }
}
